import { useState, useMemo, useEffect, useCallback } from "react";
import { useProductRegistry, PRODUCT_CATEGORIES, type RegistryProduct, type ProductCategory } from "@/data/ProductRegistryContext";
import * as ProductRegistryService from "@/services/productRegistry.service";
import { VendorService } from "@/services/vendor.service";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { toast } from "sonner";
import { Plus, Upload, Download, Search, Pencil, Package, Filter, AlertTriangle, CheckCircle2, Link2, Trash2 } from "lucide-react";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { Link } from "react-router-dom";

function generateId() {
  return "reg-" + Math.random().toString(36).slice(2, 10);
}

function normalizeSkuToken(value: string): string {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

function normalizeVendorCode(value: string): string {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 24);
}

function normalizeRegistryCategory(value: string): ProductCategory | undefined {
  const raw = String(value || "").trim();
  if (!raw) return undefined;

  if (PRODUCT_CATEGORIES.includes(raw as ProductCategory)) {
    return raw as ProductCategory;
  }

  const key = raw.toLowerCase();
  const mapped: Record<string, ProductCategory> = {
    panels: "Wall System",
    simtile: "Wall System",
    "acrylic systems": "Wall System",
    "glass/enclosure": "Glass/Enclosure",
    "shower bases": "Base",
    "custom bases": "Base",
    "sloped cores": "Base",
    floors: "Base",
    tubs: "Base",
    accessories: "Add-On",
    "custom parts": "Add-On",
    "std kits": "Add-On",
    "plumbing fixtures": "Hardware",
  };

  return mapped[key] || undefined;
}

function parseCsvRecords(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === ",") {
      row.push(cell);
      cell = "";
      continue;
    }
    if (ch === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
      continue;
    }
    if (ch === "\r") continue;
    cell += ch;
  }

  row.push(cell);
  rows.push(row);

  return rows
    .map((r) => r.map((v) => v.trim()))
    .filter((r) => r.some((v) => v.length > 0));
}

type VendorSkuImportRow = {
  vendorRef: string;
  vendorSku: string;
  vendorProductName?: string;
  internalSku: string;
  internalProductName?: string;
  registryCategory?: ProductCategory;
  productClassification?: string;
  type?: string;
  variantKey?: string;
  matchType?: ProductRegistryService.VendorSkuMatchType;
  confidence?: number;
  cost?: number;
  price?: number;
  notes?: string;
};

const AUTO_BUNDLE_SCOPES = [
  { key: "bathroom", label: "Bathroom" },
  { key: "windows", label: "Windows" },
  { key: "doors", label: "Doors" },
  { key: "flooring", label: "Flooring" },
  { key: "default", label: "Default" },
] as const;

type AutoBundleScope = typeof AUTO_BUNDLE_SCOPES[number]["key"];

function parseAutoBundleScopes(notes: string): Map<AutoBundleScope, number> {
  const scopes = new Map<AutoBundleScope, number>();
  const match = String(notes || "").match(/AUTO_BUNDLE:([^\n\r]+)/i);
  if (!match) return scopes;
  const parts = match[1]
    .split(/[;,]+/)
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean);
  for (const part of parts) {
    const [rawScope, rawQty] = part.split(/[=:]/);
    if (rawScope === "bathroom" || rawScope === "windows" || rawScope === "doors" || rawScope === "flooring" || rawScope === "default") {
      const parsedQty = Math.max(1, Number(rawQty || 1) || 1);
      scopes.set(rawScope, parsedQty);
    }
  }
  return scopes;
}

function upsertAutoBundleScopesInNotes(notes: string, scopes: Map<AutoBundleScope, number>): string {
  const cleaned = String(notes || "")
    .replace(/\s*AUTO_BUNDLE:[^\n\r]+/gi, "")
    .trim();
  if (scopes.size === 0) return cleaned;
  const marker = `AUTO_BUNDLE:${Array.from(scopes.entries())
    .map(([scope, qty]) => `${scope}=${Math.max(1, Number(qty || 1) || 1)}`)
    .join(",")
    .toUpperCase()}`;
  return cleaned ? `${cleaned}\n${marker}` : marker;
}

const EMPTY_PRODUCT: Omit<RegistryProduct, "productId"> = {
  productName: "", sku: "", vendor: "", category: "Other", subcategory: "",
  unitOfMeasure: "each", cost: 0, price: 0, trackInventory: false,
  stockQuantity: 0, reorderThreshold: 0, active: true, notes: "",
};

/* ─── CSV Bulk Import Dialog ─── */
function BulkImportDialog({ open, onOpenChange, onImport }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onImport: (rows: RegistryProduct[], mode: "overwrite" | "skip" | "merge") => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Record<string, string>[]>([]);
  const [mode, setMode] = useState<"overwrite" | "skip" | "merge">("skip");
  const [errors, setErrors] = useState<string[]>([]);

  const handleFile = (f: File) => {
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
      if (lines.length < 2) { setErrors(["File must have a header row and at least one data row"]); return; }
      const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
      const rows = lines.slice(1).map((line) => {
        const vals = line.split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
        const obj: Record<string, string> = {};
        headers.forEach((h, i) => { obj[h] = vals[i] || ""; });
        return obj;
      });
      // Validate
      const errs: string[] = [];
      const skus = new Set<string>();
      rows.forEach((r, i) => {
        if (!r.sku) errs.push(`Row ${i + 2}: Missing SKU`);
        else if (skus.has(r.sku)) errs.push(`Row ${i + 2}: Duplicate SKU "${r.sku}"`);
        if (r.sku) skus.add(r.sku);
        if (!r.productName) errs.push(`Row ${i + 2}: Missing Product Name`);
      });
      setErrors(errs);
      setPreview(rows);
    };
    reader.readAsText(f);
  };

  const handleConfirm = () => {
    if (errors.length > 0) { toast.error("Fix errors before importing"); return; }
    const parsed: RegistryProduct[] = preview.map((r) => ({
      productId: generateId(),
      productName: r.productName || "",
      sku: r.sku || "",
      vendor: r.vendor || "",
      category: (PRODUCT_CATEGORIES.includes(r.category as ProductCategory) ? r.category : "Other") as ProductCategory,
      subcategory: r.subcategory || "",
      unitOfMeasure: r.unitOfMeasure || "each",
      cost: parseFloat(r.cost) || 0,
      price: parseFloat(r.price) || 0,
      trackInventory: r.trackInventory === "true",
      stockQuantity: parseInt(r.stockQuantity) || 0,
      reorderThreshold: parseInt(r.reorderThreshold) || 0,
      active: r.active !== "false",
      notes: r.notes || "",
    }));
    onImport(parsed, mode);
    onOpenChange(false);
    setFile(null); setPreview([]); setErrors([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Bulk Import Products</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>CSV File</Label>
            <Input type="file" accept=".csv" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
            <p className="text-[10px] text-muted-foreground mt-1">
              Required columns: productName, sku, vendor, category, cost, price. Optional: subcategory, unitOfMeasure, trackInventory, stockQuantity, reorderThreshold, active, notes
            </p>
          </div>
          <div>
            <Label>Duplicate Handling</Label>
            <Select value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">Skip duplicates</SelectItem>
                <SelectItem value="overwrite">Overwrite existing by SKU</SelectItem>
                <SelectItem value="merge">Merge (update missing fields only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {errors.length > 0 && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3">
              <div className="flex items-center gap-1 text-destructive text-xs font-medium mb-1">
                <AlertTriangle className="h-3 w-3" /> {errors.length} validation error(s)
              </div>
              <ul className="text-[10px] text-destructive space-y-0.5 max-h-32 overflow-y-auto">
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
          {preview.length > 0 && (
            <div>
              <p className="text-xs font-medium mb-1">{preview.length} rows to import</p>
              <div className="border rounded-md overflow-auto max-h-48">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[10px]">Product Name</TableHead>
                      <TableHead className="text-[10px]">SKU</TableHead>
                      <TableHead className="text-[10px]">Vendor</TableHead>
                      <TableHead className="text-[10px]">Category</TableHead>
                      <TableHead className="text-[10px]">Cost</TableHead>
                      <TableHead className="text-[10px]">Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.slice(0, 10).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-[10px]">{r.productName}</TableCell>
                        <TableCell className="text-[10px] font-mono">{r.sku}</TableCell>
                        <TableCell className="text-[10px]">{r.vendor}</TableCell>
                        <TableCell className="text-[10px]">{r.category}</TableCell>
                        <TableCell className="text-[10px]">${r.cost}</TableCell>
                        <TableCell className="text-[10px]">${r.price}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {preview.length > 10 && <p className="text-[10px] text-muted-foreground p-2">...and {preview.length - 10} more</p>}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={preview.length === 0 || errors.length > 0}>
            Import {preview.length} Products
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Main Page ─── */
export default function ProductRegistryPage() {
  const { products, allVendors, refreshProducts } = useProductRegistry();
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const canEdit = hasPerm("settings_edit");

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [vendorFilter, setVendorFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [editProduct, setEditProduct] = useState<RegistryProduct | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [draft, setDraft] = useState<Omit<RegistryProduct, "productId">>(EMPTY_PRODUCT);
  const [draftBundleScopes, setDraftBundleScopes] = useState<Map<AutoBundleScope, number>>(new Map());
  const [vendors, setVendors] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [vendorSkuMappings, setVendorSkuMappings] = useState<ProductRegistryService.ProductVendorSkuMapRow[]>([]);
  const [mappingSearch, setMappingSearch] = useState("");
  const [editingVendorSkuMappingId, setEditingVendorSkuMappingId] = useState<string | null>(null);
  const [mappingDraft, setMappingDraft] = useState({
    vendorId: "",
    vendorSku: "",
    internalSku: "",
    variantKey: "",
    isActive: true,
  });
  const [showVendorSkuImport, setShowVendorSkuImport] = useState(false);
  const [vendorSkuImportRows, setVendorSkuImportRows] = useState<VendorSkuImportRow[]>([]);
  const [vendorSkuImportErrors, setVendorSkuImportErrors] = useState<string[]>([]);
  const [vendorSkuImportProgress, setVendorSkuImportProgress] = useState({
    running: false,
    total: 0,
    processed: 0,
    success: 0,
    failed: 0,
    pricing: 0,
  });

  const refreshVendorSkuMappings = async () => {
    try {
      const rows = await ProductRegistryService.getVendorSkuMappings();
      setVendorSkuMappings(rows);
    } catch (error) {
      console.error("Failed to load vendor SKU mappings:", error);
      toast.error("Failed to load vendor SKU mappings");
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const v = await VendorService.getVendors();
        if (!active) return;
        setVendors(v.map((row) => ({ id: row.id, name: row.name, code: row.code })));
      } catch (error) {
        console.error("Failed to load vendors for mapping:", error);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    refreshVendorSkuMappings();
  }, []);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (search && !p.productName.toLowerCase().includes(search.toLowerCase()) && !p.sku.toLowerCase().includes(search.toLowerCase())) return false;
      if (categoryFilter !== "all" && p.category !== categoryFilter) return false;
      if (vendorFilter !== "all" && p.vendor !== vendorFilter) return false;
      if (statusFilter === "active" && !p.active) return false;
      if (statusFilter === "inactive" && p.active) return false;
      return true;
    });
  }, [products, search, categoryFilter, vendorFilter, statusFilter]);

  const vendorNameById = useMemo(() => {
    const map = new Map<string, string>();
    vendors.forEach((v) => map.set(v.id, v.name));
    vendorSkuMappings.forEach((row) => {
      const resolvedName = String(row.vendor_name || "").trim();
      if (resolvedName && !map.has(row.vendor_id)) {
        map.set(row.vendor_id, resolvedName);
      }
    });
    return map;
  }, [vendorSkuMappings, vendors]);

  const mappingVendorOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string; code: string }>();
    vendors.forEach((vendor) => {
      map.set(vendor.id, vendor);
    });
    vendorSkuMappings.forEach((row) => {
      if (!map.has(row.vendor_id)) {
        const fallbackName = String(row.vendor_name || "").trim();
        map.set(row.vendor_id, {
          id: row.vendor_id,
          name: fallbackName || row.vendor_id,
          code: "",
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [vendorSkuMappings, vendors]);

  const filteredMappings = useMemo(() => {
    const query = mappingSearch.trim().toLowerCase();
    if (!query) return vendorSkuMappings;
    return vendorSkuMappings.filter((row) => {
      const vendorName = vendorNameById.get(row.vendor_id) || "";
      return [
        vendorName,
        row.vendor_sku,
        row.internal_sku,
        row.variant_key || "",
        row.match_type,
        row.is_active ? "active" : "inactive",
      ].some((value) => value.toLowerCase().includes(query));
    });
  }, [mappingSearch, vendorNameById, vendorSkuMappings]);

  const productPagination = usePagination(filtered, 20);
  const mappingPagination = usePagination(filteredMappings, 20);

  useEffect(() => {
    productPagination.reset();
  }, [search, categoryFilter, vendorFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    mappingPagination.reset();
  }, [filteredMappings.length, mappingSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetMappingDraft = useCallback(() => {
    setEditingVendorSkuMappingId(null);
    setMappingDraft({
      vendorId: "",
      vendorSku: "",
      internalSku: "",
      variantKey: "",
      isActive: true,
    });
  }, []);

  const stats = useMemo(() => ({
    total: products.length,
    active: products.filter((p) => p.active).length,
    lowStock: products.filter((p) => p.trackInventory && p.stockQuantity <= p.reorderThreshold).length,
  }), [products]);

  const productSkuOptions = useMemo(
    () => products.map((p) => p.sku).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [products],
  );
  const productBySku = useMemo(() => {
    const map = new Map<string, RegistryProduct>();
    products.forEach((p) => map.set(p.sku.toUpperCase(), p));
    return map;
  }, [products]);

  const vendorByRef = useMemo(() => {
    const map = new Map<string, { id: string; name: string; code: string }>();
    vendors.forEach((v) => {
      map.set(v.id.toLowerCase(), v);
      map.set(v.name.trim().toLowerCase(), v);
      if (v.code) map.set(v.code.trim().toLowerCase(), v);
    });
    return map;
  }, [vendors]);

  const addVendorSkuMapping = async () => {
    const vendorId = mappingDraft.vendorId.trim();
    const vendorSku = mappingDraft.vendorSku.trim();
    const internalSku = mappingDraft.internalSku.trim();
    if (!vendorId || !vendorSku || !internalSku) {
      toast.error("Vendor, Vendor SKU, and Internal SKU are required");
      return;
    }
    try {
      await ProductRegistryService.upsertVendorSkuMapping({
        id: editingVendorSkuMappingId || undefined,
        vendorId,
        vendorSku,
        internalSku,
        variantKey: mappingDraft.variantKey.trim() || null,
        matchType: mappingDraft.variantKey.trim() ? "variant" : "exact",
        isActive: mappingDraft.isActive,
      });
      resetMappingDraft();
      await refreshVendorSkuMappings();
      toast.success(editingVendorSkuMappingId ? "Vendor SKU mapping updated" : "Vendor SKU mapping saved");
    } catch (error) {
      console.error("Failed to save vendor SKU mapping:", error);
      toast.error("Failed to save mapping", { description: error instanceof Error ? error.message : "Unknown error" });
    }
  };

  const editVendorSkuMapping = useCallback((row: ProductRegistryService.ProductVendorSkuMapRow) => {
    setEditingVendorSkuMappingId(row.id);
    setMappingDraft({
      vendorId: row.vendor_id,
      vendorSku: row.vendor_sku,
      internalSku: row.internal_sku,
      variantKey: row.variant_key || "",
      isActive: row.is_active,
    });
  }, []);

  const removeVendorSkuMapping = useCallback(async (id: string) => {
    try {
      await ProductRegistryService.deleteVendorSkuMapping(id);
      if (editingVendorSkuMappingId === id) {
        resetMappingDraft();
      }
      await refreshVendorSkuMappings();
      toast.success("Vendor SKU mapping deleted");
    } catch (error) {
      console.error("Failed to delete vendor SKU mapping:", error);
      toast.error("Failed to delete mapping");
    }
  }, [editingVendorSkuMappingId, resetMappingDraft]);

  const downloadVendorSkuTemplate = () => {
    const headers = ["vendorRef", "vendorSku", "vendorProductName", "internalSku", "internalProductName", "registryCategory", "productClassification", "type", "variantKey", "matchType", "confidence", "cost", "price", "notes"];
    const sampleRows = [
      ["SENTREL", "SENTREL", "Sentrel Wall Panel", "", "Sentrel Standard Wall Panel Set", "Wall System", "Panel", "Set", "White", "variant", "95", "420", "899", "Sentrel white wall panel"],
      ["HOMEDEPOT", "CR", "Curtain Rod", "OTH-AUTO-CURTAIN-ROD", "Curtain Rod", "Hardware", "Trim", "Single", "", "manual_override", "100", "12.5", "39.99", "Legacy alias mapping"],
    ];
    const csvRows = [
      headers.join(","),
      ...sampleRows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vendor-sku-crosswalk-template.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const parseVendorSkuCsv = (text: string): { rows: VendorSkuImportRow[]; errors: string[] } => {
    const records = parseCsvRecords(text);
    if (records.length < 2) return { rows: [], errors: ["CSV must contain header + at least one row"] };
    const headers = records[0].map((h) => h.trim().replace(/^"|"$/g, ""));
    const indexOf = (name: string) => headers.findIndex((h) => h.toLowerCase() === name.toLowerCase());

    const idxVendorRef = indexOf("vendorRef");
    const idxVendorSku = indexOf("vendorSku");
    const idxVendorProductName = indexOf("vendorProductName");
    const idxInternalSku = indexOf("internalSku");
    const idxInternalProductName = indexOf("internalProductName");
    const idxRegistryCategory = indexOf("registryCategory");
    const idxProductCategory = indexOf("productCategory");
    const idxLegacyCategory = indexOf("category");
    const idxProductClassification = indexOf("productClassification");
    const idxClassification = indexOf("classification");
    const idxType = indexOf("type");
    const idxVariantKey = indexOf("variantKey");
    const idxMatchType = indexOf("matchType");
    const idxConfidence = indexOf("confidence");
    const idxCost = indexOf("cost");
    const idxPrice = indexOf("price");
    const idxNotes = indexOf("notes");

    const missingHeaders = [
      idxVendorRef < 0 ? "vendorRef" : null,
      idxVendorSku < 0 ? "vendorSku" : null,
    ].filter(Boolean);

    if (missingHeaders.length > 0) {
      return { rows: [], errors: [`Missing required header(s): ${missingHeaders.join(", ")}`] };
    }

    const errors: string[] = [];
    const rows: VendorSkuImportRow[] = [];

    records.slice(1).forEach((parts, i) => {
      const rowNo = i + 2;
      const vendorRef = String(parts[idxVendorRef] || "").trim();
      const vendorSku = String(parts[idxVendorSku] || "").trim();
      const vendorProductName = idxVendorProductName >= 0 ? String(parts[idxVendorProductName] || "").trim() : "";
      const internalSku = idxInternalSku >= 0 ? String(parts[idxInternalSku] || "").trim() : "";
      const internalProductName = idxInternalProductName >= 0 ? String(parts[idxInternalProductName] || "").trim() : "";
      const registryCategoryRaw = idxRegistryCategory >= 0 ? String(parts[idxRegistryCategory] || "").trim() : "";
      const productCategoryRaw = idxProductCategory >= 0 ? String(parts[idxProductCategory] || "").trim() : "";
      const legacyCategoryRaw = idxLegacyCategory >= 0 ? String(parts[idxLegacyCategory] || "").trim() : "";
      const registryCategory = registryCategoryRaw || productCategoryRaw || legacyCategoryRaw;
      const productClassificationRaw = idxProductClassification >= 0 ? String(parts[idxProductClassification] || "").trim() : "";
      const classification = idxClassification >= 0 ? String(parts[idxClassification] || "").trim() : "";
      const productClassification = productClassificationRaw || classification;
      const type = idxType >= 0 ? String(parts[idxType] || "").trim() : "";
      const variantKey = idxVariantKey >= 0 ? String(parts[idxVariantKey] || "").trim() : "";
      const rawMatchType = idxMatchType >= 0 ? String(parts[idxMatchType] || "").trim() : "";
      const rawConfidence = idxConfidence >= 0 ? String(parts[idxConfidence] || "").trim() : "";
      const rawCost = idxCost >= 0 ? String(parts[idxCost] || "").trim() : "";
      const rawPrice = idxPrice >= 0 ? String(parts[idxPrice] || "").trim() : "";
      const notes = idxNotes >= 0 ? String(parts[idxNotes] || "").trim() : "";

      if (!vendorRef) errors.push(`Row ${rowNo}: vendorRef is required`);
      if (!vendorSku) errors.push(`Row ${rowNo}: vendorSku is required`);
      if (!internalSku && !variantKey) errors.push(`Row ${rowNo}: internalSku is required when variantKey is empty`);

      const matchType = (rawMatchType || "").toLowerCase();
      const allowedMatchTypes = ["exact", "variant", "manual_override", "inferred"];
      if (matchType && !allowedMatchTypes.includes(matchType)) {
        errors.push(`Row ${rowNo}: matchType must be one of ${allowedMatchTypes.join(", ")}`);
      }
      const confidence = rawConfidence ? Number(rawConfidence) : undefined;
      if (rawConfidence && (!Number.isFinite(confidence) || confidence < 0 || confidence > 100)) {
        errors.push(`Row ${rowNo}: confidence must be a number between 0 and 100`);
      }
      const cost = rawCost ? Number(rawCost) : undefined;
      if (rawCost && (!Number.isFinite(cost) || cost < 0)) {
        errors.push(`Row ${rowNo}: cost must be a non-negative number`);
      }
      const price = rawPrice ? Number(rawPrice) : undefined;
      if (rawPrice && (!Number.isFinite(price) || price < 0)) {
        errors.push(`Row ${rowNo}: price must be a non-negative number`);
      }

      rows.push({
        vendorRef,
        vendorSku,
        vendorProductName: vendorProductName || undefined,
        internalSku,
        internalProductName: internalProductName || undefined,
        registryCategory: normalizeRegistryCategory(registryCategory),
        productClassification: productClassification || undefined,
        type: type || undefined,
        variantKey: variantKey || undefined,
        matchType: (matchType as ProductRegistryService.VendorSkuMatchType) || undefined,
        confidence: Number.isFinite(confidence) ? confidence : undefined,
        cost: Number.isFinite(cost) ? cost : undefined,
        price: Number.isFinite(price) ? price : undefined,
        notes: notes || undefined,
      });
    });

    return { rows, errors };
  };

  const handleVendorSkuCsvFile = async (file: File) => {
    setVendorSkuImportProgress({
      running: false,
      total: 0,
      processed: 0,
      success: 0,
      failed: 0,
      pricing: 0,
    });
    const text = await file.text();
    const parsed = parseVendorSkuCsv(text);
    setVendorSkuImportRows(parsed.rows);
    setVendorSkuImportErrors(parsed.errors);
    if (parsed.errors.length === 0) {
      toast.success(`Parsed ${parsed.rows.length} mapping row(s)`);
    }
  };

  const confirmVendorSkuImport = async () => {
    if (vendorSkuImportRows.length === 0) {
      toast.error("No rows parsed for import");
      return;
    }
    if (vendorSkuImportErrors.length > 0) {
      toast.error("Fix CSV validation errors before import");
      return;
    }
    let success = 0;
    let updatedPricing = 0;
    let createdProducts = 0;
    const errors: string[] = [];
    const createdVendorRefs = new Set<string>();
    const localVendorByRef = new Map(vendorByRef);

    const ensureVendorByRef = async (vendorRefRaw: string): Promise<{ id: string; name: string; code: string } | null> => {
      const key = vendorRefRaw.trim().toLowerCase();
      const existing = localVendorByRef.get(key);
      if (existing) return existing;

      const vendorName = vendorRefRaw.trim();
      if (!vendorName) return null;

      const baseCode = normalizeVendorCode(vendorRefRaw) || "VENDOR";
      const codeCandidates = [baseCode, `${baseCode}-AUTO`].slice(0, 2);

      for (const code of codeCandidates) {
        try {
          const created = await VendorService.createVendor({
            name: vendorName,
            code,
            ordering_method: "Email",
            is_active: true,
          });
          const normalized = { id: created.id, name: created.name, code: created.code };
          localVendorByRef.set(created.id.toLowerCase(), normalized);
          localVendorByRef.set(created.name.trim().toLowerCase(), normalized);
          if (created.code) localVendorByRef.set(created.code.trim().toLowerCase(), normalized);
          createdVendorRefs.add(vendorRefRaw);
          return normalized;
        } catch {
          // Try next candidate.
        }
      }

      // Refresh from DB and retry lookup in case vendor already existed with different code.
      try {
        const fresh = await VendorService.getVendors();
        const nextVendors = fresh.map((row) => ({ id: row.id, name: row.name, code: row.code }));
        setVendors(nextVendors);
        nextVendors.forEach((v) => {
          localVendorByRef.set(v.id.toLowerCase(), v);
          localVendorByRef.set(v.name.trim().toLowerCase(), v);
          if (v.code) localVendorByRef.set(v.code.trim().toLowerCase(), v);
        });
        return localVendorByRef.get(key) || null;
      } catch {
        return null;
      }
    };

    setVendorSkuImportProgress({
      running: true,
      total: vendorSkuImportRows.length,
      processed: 0,
      success: 0,
      failed: 0,
      pricing: 0,
    });

    try {
      const uniqueVendorRefs = Array.from(
        new Set(vendorSkuImportRows.map((r) => r.vendorRef.trim()).filter(Boolean)),
      );
      for (const ref of uniqueVendorRefs) {
        await ensureVendorByRef(ref);
      }

      const chunkSize = 25;
      for (let i = 0; i < vendorSkuImportRows.length; i += chunkSize) {
        const chunk = vendorSkuImportRows.slice(i, i + chunkSize);
        const chunkResults = await Promise.all(
          chunk.map(async (row) => {
            const vendor = localVendorByRef.get(row.vendorRef.trim().toLowerCase()) || await ensureVendorByRef(row.vendorRef);
            if (!vendor) {
              return { ok: false as const, err: `Unknown vendorRef: ${row.vendorRef}`, pricingUpdated: false };
            }
            try {
              const internalSkuValue = row.internalSku?.trim() || undefined;
              const resolvedInternalSku =
                internalSkuValue
                || (row.variantKey
                  ? `${normalizeSkuToken(row.vendorSku)}-${normalizeSkuToken(row.variantKey)}`
                  : "");

              if (resolvedInternalSku) {
                const existingProduct = products.find(
                  (p) => p.sku.trim().toUpperCase() === resolvedInternalSku.trim().toUpperCase(),
                );
                if (!existingProduct) {
                  await ProductRegistryService.createProduct({
                    productId: crypto.randomUUID(),
                    productName: row.internalProductName?.trim() || row.vendorProductName?.trim() || resolvedInternalSku,
                    sku: resolvedInternalSku,
                    vendor: vendor.name,
                    category: row.registryCategory || "Other",
                    subcategory: row.productClassification || row.type || "",
                    unitOfMeasure: "each",
                    cost: row.cost ?? 0,
                    price: row.price ?? (row.cost ?? 0),
                    trackInventory: true,
                    stockQuantity: 0,
                    reorderThreshold: 0,
                    active: true,
                    notes: "Auto-created from vendor SKU crosswalk import.",
                  });
                  createdProducts += 1;
                }
              }

              await ProductRegistryService.upsertVendorSkuMapping({
                vendorId: vendor.id,
                vendorSku: row.vendorSku,
                internalSku: internalSkuValue,
                variantKey: row.variantKey || null,
                matchType: row.matchType || (row.variantKey ? "variant" : "exact"),
                confidence: row.confidence ?? null,
                notes: row.notes ?? null,
              });
              let pricingUpdated = false;
              if ((row.cost !== undefined || row.price !== undefined) && resolvedInternalSku) {
                const refreshedProducts = await ProductRegistryService.getProducts();
                const product = refreshedProducts.find((p) => p.sku.trim().toUpperCase() === resolvedInternalSku.trim().toUpperCase());
                if (product) {
                  await ProductRegistryService.updateProduct(product.productId, {
                    ...product,
                    productName: row.internalProductName?.trim() || product.productName,
                    category: row.registryCategory || product.category,
                    subcategory: row.productClassification || row.type || product.subcategory || "",
                    cost: row.cost ?? product.cost,
                    price: row.price ?? product.price,
                  });
                  pricingUpdated = true;
                }
              }
              return { ok: true as const, pricingUpdated };
            } catch (error) {
              return {
                ok: false as const,
                err: `${row.vendorRef}/${row.vendorSku}/${row.internalSku}: ${error instanceof Error ? error.message : "upsert failed"}`,
                pricingUpdated: false,
              };
            }
          }),
        );

        for (const result of chunkResults) {
          if (result.ok) {
            success += 1;
            if (result.pricingUpdated) updatedPricing += 1;
          } else {
            errors.push(result.err);
          }
        }

        const processed = Math.min(vendorSkuImportRows.length, i + chunk.length);
        setVendorSkuImportProgress({
          running: true,
          total: vendorSkuImportRows.length,
          processed,
          success,
          failed: errors.length,
          pricing: updatedPricing,
        });
      }
    } finally {
      setVendorSkuImportProgress((prev) => ({ ...prev, running: false }));
    }

    await refreshProducts();
    await refreshVendorSkuMappings();
    if (createdVendorRefs.size > 0) {
      toast.info(`Auto-created ${createdVendorRefs.size} vendor(s): ${Array.from(createdVendorRefs).join(", ")}`);
    }
    if (createdProducts > 0) {
      toast.info(`Auto-created ${createdProducts} product registry item(s) from import.`);
    }
    if (errors.length > 0) {
      toast.error("Vendor SKU import completed with errors", {
        description: `${success} succeeded, ${errors.length} failed${updatedPricing > 0 ? `, ${updatedPricing} pricing updates` : ""}${createdProducts > 0 ? `, ${createdProducts} products created` : ""}`,
      });
      setVendorSkuImportErrors(errors.slice(0, 30));
      return;
    }
    toast.success(`Vendor SKU import complete: ${success} row(s)${updatedPricing > 0 ? ` · ${updatedPricing} pricing update(s)` : ""}${createdProducts > 0 ? ` · ${createdProducts} product(s) created` : ""}`);
    setShowVendorSkuImport(false);
    setVendorSkuImportRows([]);
    setVendorSkuImportErrors([]);
    setVendorSkuImportProgress({
      running: false,
      total: 0,
      processed: 0,
      success: 0,
      failed: 0,
      pricing: 0,
    });
  };

  const handleSave = async () => {
    if (!draft.productName || !draft.sku) { toast.error("Product Name and SKU are required"); return; }
    const notesWithBundle = upsertAutoBundleScopesInNotes(draft.notes, draftBundleScopes);
    const nextDraft = { ...draft, notes: notesWithBundle };
    try {
      if (editProduct) {
        await ProductRegistryService.updateProduct(editProduct.productId, {
          ...nextDraft,
          productId: editProduct.productId,
        });
        toast.success("Product updated");
      } else {
        const exists = products.find((p) => p.sku.trim().toUpperCase() === draft.sku.trim().toUpperCase());
        if (exists) { toast.error("SKU already exists"); return; }
        await ProductRegistryService.createProduct({ ...nextDraft, productId: crypto.randomUUID() });
        toast.success("Product added");
      }
      await refreshProducts();
      setEditProduct(null); setShowAdd(false); setDraft(EMPTY_PRODUCT); setDraftBundleScopes(new Map());
    } catch (error) {
      console.error("Failed to save product registry item:", error);
      toast.error("Failed to save product", { description: error instanceof Error ? error.message : "Unknown error" });
    }
  };

  const handleImport = async (rows: RegistryProduct[], mode: "overwrite" | "skip" | "merge") => {
    try {
      const result = await ProductRegistryService.bulkImportProducts(rows, mode);
      await refreshProducts();
      toast.success("Products imported", {
        description: `${result.inserted} inserted, ${result.updated} updated, ${result.skipped} skipped`,
      });
    } catch (error) {
      console.error("Failed to import product registry rows:", error);
      toast.error("Import failed", { description: error instanceof Error ? error.message : "Unknown error" });
    }
  };

  const exportCsv = () => {
    const headers = ["productName","sku","vendor","category","subcategory","unitOfMeasure","cost","price","trackInventory","stockQuantity","reorderThreshold","active","notes"];
    const csvRows = [headers.join(","), ...filtered.map((p) =>
      headers.map((h) => `"${String((p as any)[h] ?? "").replace(/"/g, '""')}"`).join(",")
    )];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "product-registry.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const margin = (cost: number, price: number) => price > 0 ? ((price - cost) / price * 100).toFixed(1) : "—";

  const openEdit = (p: RegistryProduct) => {
    setEditProduct(p);
    setDraft({ ...p });
    setDraftBundleScopes(parseAutoBundleScopes(p.notes || ""));
    setShowAdd(true);
  };

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold flex items-center gap-2"><Package className="h-5 w-5" /> Product Registry</h1>
          <p className="text-xs text-muted-foreground">Master database for all materials, SKUs, vendors, and pricing</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" asChild>
              <Link to="/product-registry/auto-mapped-skus">Auto-Mapped SKU Report</Link>
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowImport(true)}>
              <Upload className="h-3 w-3 mr-1" /> Bulk Import
            </Button>
            <Button size="sm" variant="outline" onClick={exportCsv}>
              <Download className="h-3 w-3 mr-1" /> Export CSV
            </Button>
            <Button size="sm" onClick={() => { setEditProduct(null); setDraft(EMPTY_PRODUCT); setDraftBundleScopes(new Map()); setShowAdd(true); }}>
              <Plus className="h-3 w-3 mr-1" /> Add Product
            </Button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold">{stats.total}</p>
          <p className="text-[10px] text-muted-foreground">Total Products</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-success">{stats.active}</p>
          <p className="text-[10px] text-muted-foreground">Active</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-warning">{stats.lowStock}</p>
          <p className="text-[10px] text-muted-foreground">Low Stock</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input className="pl-8 h-8 text-xs" placeholder="Search by name or SKU..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[150px] h-8 text-xs"><Filter className="h-3 w-3 mr-1" /><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {PRODUCT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={vendorFilter} onValueChange={setVendorFilter}>
          <SelectTrigger className="w-[150px] h-8 text-xs"><SelectValue placeholder="Vendor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Vendors</SelectItem>
            {allVendors.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[120px] h-8 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px]">Product Name</TableHead>
                <TableHead className="text-[10px]">SKU</TableHead>
                <TableHead className="text-[10px]">Vendor</TableHead>
                <TableHead className="text-[10px]">Category</TableHead>
                <TableHead className="text-[10px] text-right">Cost</TableHead>
                <TableHead className="text-[10px] text-right">Price</TableHead>
                <TableHead className="text-[10px] text-right">Margin</TableHead>
                <TableHead className="text-[10px] text-right">Stock</TableHead>
                <TableHead className="text-[10px]">Status</TableHead>
                {canEdit && <TableHead className="text-[10px]" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {productPagination.paged.map((p) => (
                <TableRow key={p.productId} className={!p.active ? "opacity-50" : ""}>
                  <TableCell className="text-xs font-medium">{p.productName}</TableCell>
                  <TableCell className="text-[10px] font-mono text-muted-foreground">{p.sku}</TableCell>
                  <TableCell className="text-xs">{p.vendor}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[9px]">{p.category}</Badge></TableCell>
                  <TableCell className="text-xs text-right font-mono">${p.cost.toFixed(2)}</TableCell>
                  <TableCell className="text-xs text-right font-mono">${p.price.toFixed(2)}</TableCell>
                  <TableCell className="text-xs text-right font-mono">{margin(p.cost, p.price)}%</TableCell>
                  <TableCell className="text-right">
                    {p.trackInventory ? (
                      <span className={`text-xs font-mono ${p.stockQuantity <= p.reorderThreshold ? "text-warning font-bold" : ""}`}>
                        {p.stockQuantity}
                      </span>
                    ) : <span className="text-[10px] text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    {p.active
                      ? <Badge className="text-[9px] bg-success/15 text-success border-success/30">Active</Badge>
                      : <Badge variant="secondary" className="text-[9px]">Inactive</Badge>}
                    {parseAutoBundleScopes(p.notes || "").size > 0 && (
                      <Badge variant="outline" className="text-[9px] ml-1">Auto Bundle</Badge>
                    )}
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEdit(p)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {productPagination.totalItems === 0 && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 10 : 9} className="text-center text-xs text-muted-foreground py-8">
                    No products match your filters
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <PaginationBar {...productPagination} />
      </Card>
      <p className="text-[10px] text-muted-foreground text-right">{filtered.length} of {products.length} products</p>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Vendor SKU Crosswalk</h2>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Map vendor invoice/portal SKUs to internal SKUs. Supports one vendor SKU mapping to multiple internal SKUs using variant key.
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={downloadVendorSkuTemplate}>
              <Download className="h-3 w-3 mr-1" /> Download Template
            </Button>
            {canEdit && (
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowVendorSkuImport(true)}>
                <Upload className="h-3 w-3 mr-1" /> Import CSV
              </Button>
            )}
          </div>

          <div className="max-w-sm">
            <Label className="text-xs">Search Crosswalk</Label>
            <Input
              className="h-8 text-xs"
              value={mappingSearch}
              onChange={(e) => setMappingSearch(e.target.value)}
              placeholder="Search vendor, vendor SKU, internal SKU..."
            />
          </div>

          {canEdit && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
              <div>
                <Label className="text-xs">Vendor</Label>
                <Select value={mappingDraft.vendorId} onValueChange={(v) => setMappingDraft((prev) => ({ ...prev, vendorId: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>
                    {mappingVendorOptions.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Vendor SKU</Label>
                <Input
                  className="h-8 text-xs font-mono"
                  value={mappingDraft.vendorSku}
                  onChange={(e) => setMappingDraft((prev) => ({ ...prev, vendorSku: e.target.value.toUpperCase() }))}
                  placeholder="e.g. SENTREL"
                />
              </div>
              <div>
                <Label className="text-xs">Internal SKU</Label>
                <Select value={mappingDraft.internalSku} onValueChange={(v) => setMappingDraft((prev) => ({ ...prev, internalSku: v }))}>
                  <SelectTrigger className="h-8 text-xs font-mono"><SelectValue placeholder="Select internal SKU" /></SelectTrigger>
                  <SelectContent>
                    {productSkuOptions.map((sku) => (
                      <SelectItem key={sku} value={sku}>{sku}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Variant Key (optional)</Label>
                <Input
                  className="h-8 text-xs"
                  value={mappingDraft.variantKey}
                  onChange={(e) => setMappingDraft((prev) => ({ ...prev, variantKey: e.target.value }))}
                  placeholder="color/finish"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" className="h-8" onClick={addVendorSkuMapping}>
                  {editingVendorSkuMappingId ? <Pencil className="h-3 w-3 mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                  {editingVendorSkuMappingId ? "Save Edit" : "Add Mapping"}
                </Button>
                {editingVendorSkuMappingId && (
                  <Button type="button" variant="outline" size="sm" className="h-8" onClick={resetMappingDraft}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          )}

          {canEdit && (
            <div className="flex items-center gap-2">
              <Switch
                checked={mappingDraft.isActive}
                onCheckedChange={(checked) => setMappingDraft((prev) => ({ ...prev, isActive: checked }))}
              />
              <Label className="text-xs">{mappingDraft.isActive ? "Mapping active" : "Mapping inactive"}</Label>
            </div>
          )}

          <div className="border rounded-md overflow-auto">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px]">Vendor</TableHead>
                  <TableHead className="text-[10px]">Vendor SKU</TableHead>
                  <TableHead className="text-[10px]">Internal SKU</TableHead>
                  <TableHead className="text-[10px]">Registry Category</TableHead>
                  <TableHead className="text-[10px]">Product Classification</TableHead>
                  <TableHead className="text-[10px]">Variant Key</TableHead>
                  <TableHead className="text-[10px]">Mapping Type</TableHead>
                  <TableHead className="text-[10px]">Status</TableHead>
                  {canEdit && <TableHead className="text-[10px] text-right">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappingPagination.paged.map((row) => {
                  const product = productBySku.get((row.internal_sku || "").toUpperCase());
                  const vendorLabel = row.vendor_name || vendorNameById.get(row.vendor_id) || row.vendor_id;
                  const registryCategory = product?.category || "—";
                  const rawClassification = product?.subcategory?.trim() || "";
                  const classification =
                    rawClassification && rawClassification.toLowerCase() !== "vendor sku import"
                      ? rawClassification
                      : "—";
                  return (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs max-w-[140px] truncate" title={vendorLabel}>
                      {vendorLabel}
                    </TableCell>
                    <TableCell className="text-[10px] font-mono max-w-[180px] truncate" title={row.vendor_sku}>
                      {row.vendor_sku}
                    </TableCell>
                    <TableCell className="text-[10px] font-mono max-w-[200px] truncate" title={row.internal_sku}>
                      {row.internal_sku}
                    </TableCell>
                    <TableCell className="text-xs">
                      {registryCategory !== "—" ? <Badge variant="outline" className="text-[9px]">{registryCategory}</Badge> : "—"}
                    </TableCell>
                    <TableCell className="text-xs max-w-[180px] truncate" title={classification}>
                      {classification}
                    </TableCell>
                    <TableCell className="text-xs max-w-[140px] truncate" title={row.variant_key || "—"}>
                      {row.variant_key || "—"}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-[9px]">{row.match_type}</Badge></TableCell>
                    <TableCell>
                      {row.is_active
                        ? <Badge className="text-[9px] bg-success/15 text-success border-success/30">Active</Badge>
                        : <Badge variant="secondary" className="text-[9px]">Inactive</Badge>}
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => editVendorSkuMapping(row)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeVendorSkuMapping(row.id)}>
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                )})}
                {mappingPagination.totalItems === 0 && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 9 : 8} className="text-center text-xs text-muted-foreground py-6">
                      {mappingSearch.trim() ? "No vendor SKU mappings match your search" : "No vendor SKU mappings yet"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <PaginationBar {...mappingPagination} />
          <p className="text-[10px] text-muted-foreground text-right">
            {filteredMappings.length} of {vendorSkuMappings.length} mappings
          </p>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={(o) => { setShowAdd(o); if (!o) setEditProduct(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editProduct ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Product Name *</Label><Input className="h-8 text-xs" value={draft.productName} onChange={(e) => setDraft({ ...draft, productName: e.target.value })} /></div>
              <div><Label className="text-xs">SKU *</Label><Input className="h-8 text-xs font-mono" value={draft.sku} onChange={(e) => setDraft({ ...draft, sku: e.target.value.toUpperCase() })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Vendor</Label><Input className="h-8 text-xs" value={draft.vendor} onChange={(e) => setDraft({ ...draft, vendor: e.target.value })} /></div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v as ProductCategory })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRODUCT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Subcategory</Label><Input className="h-8 text-xs" value={draft.subcategory} onChange={(e) => setDraft({ ...draft, subcategory: e.target.value })} /></div>
              <div>
                <Label className="text-xs">Unit of Measure</Label>
                <Select value={draft.unitOfMeasure} onValueChange={(v) => setDraft({ ...draft, unitOfMeasure: v })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="each">Each</SelectItem>
                    <SelectItem value="case">Case</SelectItem>
                    <SelectItem value="kit">Kit</SelectItem>
                    <SelectItem value="sqft">Sq Ft</SelectItem>
                    <SelectItem value="linear_ft">Linear Ft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2 pb-0.5">
                <Switch checked={draft.active} onCheckedChange={(c) => setDraft({ ...draft, active: c })} />
                <Label className="text-xs">{draft.active ? "Active" : "Inactive"}</Label>
              </div>
            </div>
            <div className="border-t pt-3">
              <p className="text-xs font-medium mb-2">Pricing</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Cost ($)</Label><Input type="number" className="h-8 text-xs" value={draft.cost} onChange={(e) => setDraft({ ...draft, cost: parseFloat(e.target.value) || 0 })} /></div>
                <div><Label className="text-xs">Price ($)</Label><Input type="number" className="h-8 text-xs" value={draft.price} onChange={(e) => setDraft({ ...draft, price: parseFloat(e.target.value) || 0 })} /></div>
                <div>
                  <Label className="text-xs">Margin</Label>
                  <div className="h-8 flex items-center text-xs font-mono bg-muted rounded-md px-2">
                    {margin(draft.cost, draft.price)}%
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Switch checked={draft.trackInventory} onCheckedChange={(c) => setDraft({ ...draft, trackInventory: c })} />
                <Label className="text-xs">Track Inventory</Label>
              </div>
              {draft.trackInventory && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Stock Quantity</Label><Input type="number" className="h-8 text-xs" value={draft.stockQuantity} onChange={(e) => setDraft({ ...draft, stockQuantity: parseInt(e.target.value) || 0 })} /></div>
                  <div><Label className="text-xs">Reorder Threshold</Label><Input type="number" className="h-8 text-xs" value={draft.reorderThreshold} onChange={(e) => setDraft({ ...draft, reorderThreshold: parseInt(e.target.value) || 0 })} /></div>
                </div>
              )}
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea className="text-xs" rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></div>
            <div className="border-t pt-3 space-y-2">
              <p className="text-xs font-medium">Auto Bundle Inclusion</p>
              <p className="text-[10px] text-muted-foreground">
                Select project types where this product is auto-included during Build Review.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {AUTO_BUNDLE_SCOPES.map((scope) => {
                  const checked = draftBundleScopes.has(scope.key);
                  const qty = draftBundleScopes.get(scope.key) ?? 1;
                  return (
                    <label key={scope.key} className="flex items-center gap-2 rounded border px-2 py-1.5">
                      <Switch
                        checked={checked}
                        onCheckedChange={(next) =>
                          setDraftBundleScopes((prev) => {
                            const updated = new Map(prev);
                            if (next) updated.set(scope.key, updated.get(scope.key) ?? 1);
                            else updated.delete(scope.key);
                            return updated;
                          })
                        }
                      />
                      <span className="text-xs">{scope.label}</span>
                      <Input
                        type="number"
                        min={1}
                        className="h-7 w-16 text-xs ml-auto"
                        value={qty}
                        disabled={!checked}
                        onChange={(e) =>
                          setDraftBundleScopes((prev) => {
                            const updated = new Map(prev);
                            updated.set(scope.key, Math.max(1, Number(e.target.value || 1) || 1));
                            return updated;
                          })
                        }
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAdd(false); setEditProduct(null); }}>Cancel</Button>
            <Button onClick={handleSave}><CheckCircle2 className="h-3 w-3 mr-1" /> {editProduct ? "Save Changes" : "Add Product"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import */}
      <BulkImportDialog open={showImport} onOpenChange={setShowImport} onImport={handleImport} />

      <Dialog open={showVendorSkuImport} onOpenChange={setShowVendorSkuImport}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Vendor SKU Crosswalk</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleVendorSkuCsvFile(file);
                }}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Required headers: <span className="font-mono">vendorRef,vendorSku,internalSku</span>. Optional: <span className="font-mono">vendorProductName,internalProductName,registryCategory,productClassification,type,variantKey,matchType,confidence,cost,price,notes</span>. Legacy aliases still accepted: <span className="font-mono">productCategory,classification,category</span>.
              </p>
            </div>
            {vendorSkuImportErrors.length > 0 && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3">
                <div className="flex items-center gap-1 text-destructive text-xs font-medium mb-1">
                  <AlertTriangle className="h-3 w-3" /> {vendorSkuImportErrors.length} error(s)
                </div>
                <ul className="text-[10px] text-destructive space-y-0.5 max-h-36 overflow-y-auto">
                  {vendorSkuImportErrors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}
            {vendorSkuImportRows.length > 0 && (
              <div className="border rounded-md overflow-auto max-h-52">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[10px]">Vendor Ref</TableHead>
                      <TableHead className="text-[10px]">Vendor SKU</TableHead>
                      <TableHead className="text-[10px]">Vendor Name</TableHead>
                      <TableHead className="text-[10px]">Internal SKU</TableHead>
                      <TableHead className="text-[10px]">Internal Name</TableHead>
                      <TableHead className="text-[10px]">Variant</TableHead>
                      <TableHead className="text-[10px] text-right">Cost</TableHead>
                      <TableHead className="text-[10px] text-right">Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vendorSkuImportRows.slice(0, 20).map((row, idx) => (
                      <TableRow key={`${row.vendorRef}-${row.vendorSku}-${row.internalSku}-${idx}`}>
                        <TableCell className="text-xs max-w-[120px] truncate" title={row.vendorRef}>{row.vendorRef}</TableCell>
                        <TableCell className="text-[10px] font-mono max-w-[160px] truncate" title={row.vendorSku}>{row.vendorSku}</TableCell>
                        <TableCell className="text-xs max-w-[220px] truncate" title={row.vendorProductName || "—"}>{row.vendorProductName || "—"}</TableCell>
                        <TableCell className="text-[10px] font-mono max-w-[180px] truncate" title={row.internalSku}>{row.internalSku}</TableCell>
                        <TableCell className="text-xs max-w-[220px] truncate" title={row.internalProductName || "—"}>{row.internalProductName || "—"}</TableCell>
                        <TableCell className="text-xs max-w-[140px] truncate" title={row.variantKey || "—"}>{row.variantKey || "—"}</TableCell>
                        <TableCell className="text-xs text-right">{row.cost !== undefined ? `$${row.cost}` : "—"}</TableCell>
                        <TableCell className="text-xs text-right">{row.price !== undefined ? `$${row.price}` : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {vendorSkuImportRows.length > 20 && (
                  <p className="text-[10px] text-muted-foreground p-2">
                    ...and {vendorSkuImportRows.length - 20} more row(s)
                  </p>
                )}
              </div>
            )}
            {(vendorSkuImportProgress.total > 0 || vendorSkuImportProgress.running) && (
              <div className="rounded-md border p-3 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium">Import Progress</span>
                  <span>
                    {vendorSkuImportProgress.processed}/{vendorSkuImportProgress.total}
                  </span>
                </div>
                <div className="h-2 rounded bg-muted overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{
                      width: `${vendorSkuImportProgress.total > 0
                        ? Math.round((vendorSkuImportProgress.processed / vendorSkuImportProgress.total) * 100)
                        : 0}%`,
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                  <span>Success: {vendorSkuImportProgress.success}</span>
                  <span>Failed: {vendorSkuImportProgress.failed}</span>
                  <span>Pricing updates: {vendorSkuImportProgress.pricing}</span>
                  <span>{vendorSkuImportProgress.running ? "Processing..." : "Idle"}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowVendorSkuImport(false)}
              disabled={vendorSkuImportProgress.running}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmVendorSkuImport}
              disabled={vendorSkuImportRows.length === 0 || vendorSkuImportErrors.length > 0 || vendorSkuImportProgress.running}
            >
              Import {vendorSkuImportRows.length} Mapping(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
